import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@crazyegginc/hatch';

import { CURRENCIES } from '/src/features/_global/constants';
import { supportedCurrencyCodesQuery } from '/src/features/_global/queries';

const popularCurrencies = [...Object.values(CURRENCIES)];

export function CurrencySelector({ value, onSelect = null, unitOnly = false, selectFirst = false, ...rest }) {
  const { popperClassName, ...selectProps } = rest;
  const { data, isFetching } = useQuery(supportedCurrencyCodesQuery());

  const currenciesOptions = useMemo(() => {
    return buildCurrencyOptions({
      supportedCurrencyCodes: data?.supportedCurrencyCodes ?? [],
      value,
      unitOnly,
    });
  }, [data?.supportedCurrencyCodes, unitOnly, value]);

  return (
    <Select
      options={currenciesOptions}
      value={selectFirst && !value ? currenciesOptions[0]?.value : value}
      placeholder="Select a currency"
      disabled={isFetching}
      onChange={(value) => {
        onSelect?.(value);
      }}
      renderInPortal={true}
      popperClassName={`!z-[100000] [&>*[role='listbox']]:!max-h-[220px] ${popperClassName || ''}`}
      {...selectProps}
    />
  );
}

export function buildCurrencyOptions({ supportedCurrencyCodes = [], value, unitOnly = false }) {
  const supportedCodes = new Set(supportedCurrencyCodes.map(({ code }) => code));
  const supportedCurrenciesByCode = new Map(supportedCurrencyCodes.map((currency) => [currency.code, currency]));
  const popularOptions = popularCurrencies
    .filter((code) => supportedCodes.has(code))
    .map((code) => buildCurrencyOption({ currency: supportedCurrenciesByCode.get(code), unitOnly }));
  const remainingOptions = supportedCurrencyCodes
    .filter(({ code }) => !popularCurrencies.includes(code))
    .map((currency) => buildCurrencyOption({ currency, unitOnly }));
  const options = [
    ...popularOptions,
    ...(remainingOptions.length > 0
      ? [{ value: '', label: unitOnly ? '----------' : '----------------------', disabled: true }]
      : []),
    ...remainingOptions,
  ];

  if (value && !supportedCodes.has(value)) {
    return [buildCurrencyOption({ currency: { code: value, name: value }, unitOnly, disabled: true }), ...options];
  }

  return options;
}

function buildCurrencyOption({ currency, unitOnly, disabled = false }) {
  return {
    value: currency.code,
    label: `${currency.code}${!unitOnly ? ` | ${currency.name}` : ''}`,
    disabled,
  };
}
